import { AnalysisDataDTO } from "@models/platform-analysis/entities/steps/feature";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { useTheme } from "@mui/material";
import { PDFTable } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables";
import { PDFTableBody } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-body";
import { PDFTableCell } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-cell";
import { HorizontalLine } from "@pages/platform-analysis/components/buttons/export-pdf/components/line";
import { PDFSummaryAnalysisHeader } from "@pages/platform-analysis/components/buttons/export-pdf/components/tables/components/summary-analysis-header";
import { PDFSummaryOverallScore } from "@pages/platform-analysis/components/buttons/export-pdf/components/tables/components/summary-overall-score";
import {
  DEFAULT_GAP_3,
  TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import {
  PDFAnalysisSectionDTO,
  PDFFeatureAnalysis,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import { useStyles } from "@pages/platform-analysis/components/buttons/export-pdf/styles";
import { generateFeatureSectionIconUrl } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { PDFReviewTableRow } from "@pages/reviews/components/buttons/export-pdf/layouts/tables/table-row";
import { Image, Text, View } from "@react-pdf/renderer";
import { defaultTo, isEmpty, isNil } from "lodash";
import { PDFCheckSvg } from "@pages/platform-analysis/components/buttons/export-pdf/svgs/CheckSvg";
import { PDFCrossSvg } from "@pages/platform-analysis/components/buttons/export-pdf/svgs/CrossSvg";
import { PDFFeatureAnalysisTableConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/feature-analysis/configs";

interface PDFFeatureAnalysisTableProps {
  featureAnalysis?: PDFFeatureAnalysis;
  isTableExpand?: boolean;
  tableWidth?: boolean;
  showSubSection?: boolean;
  isDetailMode?: boolean;
  showAnalysisScore?: boolean;
}

export const PDFFeatureAnalysisSection = ({
  featureAnalysis,
  isTableExpand = false,
  showSubSection = false,
  isDetailMode = false,
  showAnalysisScore = false,
}: PDFFeatureAnalysisTableProps) => {
  const { typographyStyle } = useStyles();
  const { palette } = useTheme();

  const sections = featureAnalysis?.sections ?? [];
  const overallScore = featureAnalysis?.overallScore;
  const selectedProducts = featureAnalysis?.selectedProducts ?? [];

  const cellWidth = Configs.CellWidth;
  let firstCellWidth = Configs.FirstCellWidth;
  if (isDetailMode) firstCellWidth += cellWidth;

  const totalSelectionCellWidth = cellWidth;

  let tableWidth = firstCellWidth + selectedProducts?.length! * cellWidth;
  if (!isDetailMode) tableWidth += totalSelectionCellWidth;

  const renderPlatformValue = (value?: AnalysisDataValue) => {
    if (value === AnalysisDataValue.True) return <PDFCheckSvg />;
    if (value === AnalysisDataValue.False) return <PDFCrossSvg />;
    if (value === AnalysisDataValue.MissingData || isEmpty(value))
      return (
        <Text style={{ ...typographyStyle.body2, color: palette.text.primary }}>
          No data
        </Text>
      );
  };

  const renderImage = (
    isSubSectionType: boolean,
    section: PDFAnalysisSectionDTO,
  ) => {
    if (!isSubSectionType && !isNil(section?.iconUrl)) {
      const src = generateFeatureSectionIconUrl(section?.iconUrl);
      return <Image style={{ width: 10, height: 10 }} src={src} />;
    }
  };

  return (
    <>
      <View style={{ height: TITLE_HEIGHT }}>
        <Text
          style={{
            ...typographyStyle.title,
            color: palette.common.black,
          }}
        >
          {!isTableExpand ? "Feature analysis" : "Feature analysis (continued)"}
        </Text>
        <HorizontalLine color={palette.common.black} />
      </View>

      <PDFTable tableWidth={tableWidth}>
        {/* Header */}
        <PDFSummaryAnalysisHeader
          isFeatureAnalysis
          isDetailMode={isDetailMode}
          selectedProducts={featureAnalysis?.selectedProducts}
        />
        {/* Body */}
        <PDFTableBody>
          {/* Overall Score  */}
          {!isTableExpand && (
            <PDFSummaryOverallScore
              isFeatureAnalysis
              isDetailMode={isDetailMode}
              isShowScore={showAnalysisScore}
              overallScore={overallScore}
              color={isDetailMode ? "primary" : "dark"}
            />
          )}
          {sections?.map((section, index) => {
            const isSubSectionType = section?.type === "SubSection";
            const isFeatureType = section?.type === "Feature";
            const colorRowDetailMode =
              isSubSectionType && showSubSection
                ? "gray"
                : isFeatureType
                ? "white"
                : "dark";
            const colorRow =
              !isSubSectionType && showSubSection ? "gray" : "white";
            const isLastRow = index === defaultTo(sections?.length, 0) - 1;

            return (
              <>
                <PDFReviewTableRow
                  key={index}
                  height={
                    section?.name?.length! > Configs.MaxCharsPerLine
                      ? Configs.DoubleRowHeight
                      : Configs.SingleRowHeight
                  }
                  minHeight={isLastRow ? "auto" : Configs.SingleRowHeight}
                  color={isDetailMode ? colorRowDetailMode : colorRow}
                >
                  <PDFTableCell alignItems="flex-start" width={firstCellWidth}>
                    <View
                      style={{
                        gap: DEFAULT_GAP_3,
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {renderImage(isSubSectionType, section)}
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color: palette.text.primary,
                          fontWeight:
                            isSubSectionType || isFeatureType
                              ? "normal"
                              : "bold",
                        }}
                      >
                        {section?.name}
                        {isDetailMode &&
                          !isFeatureType &&
                          ` (${section?.totalSelectedFeature}/${section?.totalFeature})`}
                      </Text>
                    </View>
                  </PDFTableCell>
                  {!isDetailMode && (
                    <PDFTableCell width={cellWidth}>
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color: palette.text.primary,
                          fontWeight: isSubSectionType ? "normal" : "bold",
                        }}
                      >
                        {`${section?.totalSelectedFeature}/${section?.totalFeature}`}
                      </Text>
                    </PDFTableCell>
                  )}
                  {section?.analysisData?.map(
                    (product: AnalysisDataDTO, index: number) => (
                      <PDFTableCell
                        key={index}
                        width={cellWidth}
                        hasPadding={false}
                      >
                        {isFeatureType
                          ? renderPlatformValue(
                              product.value as AnalysisDataValue,
                            )
                          : (!isDetailMode ||
                              (isDetailMode && showAnalysisScore)) && (
                              <Text
                                style={{
                                  ...typographyStyle.body2,
                                  color: palette.text.primary,
                                  fontWeight:
                                    isSubSectionType || isFeatureType
                                      ? "normal"
                                      : "bold",
                                }}
                              >
                                {product?.totalScore} %
                              </Text>
                            )}
                      </PDFTableCell>
                    ),
                  )}
                </PDFReviewTableRow>
              </>
            );
          })}
        </PDFTableBody>
      </PDFTable>
    </>
  );
};
